import axios from 'axios'
import { get } from 'svelte/store';
import { uniq, chunk, difference, isEmpty } from "lodash";
import { user, detected_fonts, user_categorized_families, server_path, local_fonts } from "../stores";

const API_KEY = process.env.OPENAI_API_KEY;
const client = axios.create({ headers: { Authorization: "Bearer " + API_KEY } })

// db read batch sizes
const db_read_batch_size = 100
// openai and db write batch size
const openai_batch_size = 40
// db read wait time
const db_read_wait_time = 100

// we will ask openai to classify within these categories
const categories = "serif, sans-serif, script, handwriting, monospaced or symbol"

// adjust max_tokens based on the batch size (~10 tokens per font + 10 buffer = 20 per font)
let params = {
  model: "gpt-4o-mini",
  response_format:{ "type": "json_object" },
  temperature: 0.2,
  frequency_penalty: 0,
  presence_penalty: 0,
  max_tokens: 20 * openai_batch_size,
};

export const getFontClassificationsFromDB = async (passed_user) => {

  console.log('Classifier - Starting font classification routine')

  // set user to stored if it is not passed
  if (!passed_user) passed_user = get(user)

  // check if user has local fonts
  if (!get(local_fonts)) {
    console.log('Classifier - User has no local fonts, exiting')
    return false
  } 
    
  // check if user turned off feature flag
  if (!passed_user.feature_ai_categories_on) { 
    console.log('Classifier - User turned off AI categories, exiting')
    return false
  }
  
  // get uncategorized font family names
  let uncategorized_font_family_names = getUnClassifiedFontFamilyNames()

  // exit if no uncategorized fonts
  if (uncategorized_font_family_names.length === 0) {
    console.log('Classifier - All detected fonts classification exists in local storage')
    addFontCategoriesToDetectedFonts()
  }
  else {
    console.log('Classifier - Requesting for classifications from DB')
  }
  
  // divide into chunks
  const chunks = chunk(uncategorized_font_family_names, db_read_batch_size)

  // map each chunks to a promise that resolves when it's categorized
  const promises = chunks.map(async (chunk, index) => {
    // await to avoid db clogging
    await new Promise(resolve => setTimeout(resolve, index * db_read_wait_time))
    
    // check if font classification exists in database
    let form_data = new FormData()
    for (let i = 0; i < chunk.length; i++) {
      form_data.append('family_names[]', chunk[i])
    }
    let response = await axios.post(get(server_path) + 'classifier/get_many.php', form_data, { withCredentials: true })
    
    if (!!response.data) {
      // add new classifications to user_categorized_families
      let already_categorized = get(user_categorized_families) || []
      user_categorized_families.set([...already_categorized, ...response.data])
      // add font categories to detected fonts
      addFontCategoriesToDetectedFonts()
    }
  })

  // wait for all promises to resolve before continuing
  await Promise.all(promises)

  // classifications are received from the DB
  console.log('Classifier - Received available classifications from DB')

  // check uncategorized font family names again
  uncategorized_font_family_names = getUnClassifiedFontFamilyNames()

  // finish if no uncategorized fonts
  if (uncategorized_font_family_names.length === 0) {
    // addFontCategoriesToDetectedFonts()
    console.log('Classifier - All font classifications received')
  }
  else {
    // get uncategorized font family names from openai
    getFontClassificationsFromOpenAI(uncategorized_font_family_names)
  }

};

const getFontClassificationsFromOpenAI = async (uncategorized_font_family_names) => {
  
  console.log('Classifier - Requesting remaining classifications from OPENAI')
  console.log(uncategorized_font_family_names)
  
  // divide into chunks
  const chunks = chunk(uncategorized_font_family_names, openai_batch_size)

  // map each chunks to a promise that resolves when it's categorized
  const promises = chunks.map(async (chunk) => {
    
    // prepare prompt
    const family_names_string = chunk.join(', ')
    // chat prompt
    params.messages = [
      { "role": "system", "content": `As an expert in typography and font classifications your task is to assign the given fonts explicitly to one of these categories: ${categories}. Return your response in JSON format` },
      { "role": "user", "content": "Helvetica, Times" },
      { "role": "assistant", "content": "{\"Helvetica\":\"sans-serif\", \"Times\":\"serif\"}" },
      { "role": "user", "content": `${family_names_string}` }
    ]

    // send request to openai
    const result = await client.post("https://api.openai.com/v1/chat/completions", params)
  
    // check for errors
    if (!result.data.choices[0].message.content) {
      console.error('Classifier - OpenAI returned an error: ' + result.data.error)
      return
    }

    // locate the result message content
    let result_json_str = result.data.choices[0].message.content

    // check if result is valid json
    const newly_categorized_families = parseJSON(result_json_str)

    if (newly_categorized_families) {
      
      // if valid, save to database
      console.log('Classifier - Received classification batch from OpenAI, initiating save to DB')
      saveClassificationsToDB(newly_categorized_families)
      
      // convert object to array
      const newly_categorized_families_array = Object.entries(newly_categorized_families).map(([family_name, category]) => {
        return { family_name, category };
      })

      // add new classifications to user_categorized_families
      let already_categorized = get(user_categorized_families) || []
      user_categorized_families.set([...already_categorized, ...newly_categorized_families_array])
    }
    else {
      // if not valid json, skip classification
      console.error('invalid json: ' + result_json_str)
      return
    }
  })

  // wait for all promises to resolve before continuing
  await Promise.all(promises)

  // classifications are received from openai
  console.log('Classifier - Received missing classifications from OpenAI')
  
  // add font categories to detected fonts
  addFontCategoriesToDetectedFonts()

}

// save classifications to database
const saveClassificationsToDB = async (newly_categorized_families) => {

  // prepare form data
  let form_data = new FormData()
  
  // loop through newly_categorized_families and prepare data to send to server
  const fonts = [];
  for (const [family_name, category] of Object.entries(newly_categorized_families)) {
    fonts.push({ family_name, category })
  }
  form_data.append('fonts', JSON.stringify(fonts))
  const response = axios.post(get(server_path) + 'classifier/save_many.php', form_data, { withCredentials: true })
  if (response) {
    console.log('Classifier - Saved classifications to DB')
  } else {
    console.error('Classifier - Failed to save classifications to DB')
  }
}

// add font categories to detected fonts
const addFontCategoriesToDetectedFonts = () => {
  // loop through detected fonts object and add font category where family_name matches
  get(detected_fonts).forEach((font) => {
    const font_family_name = font.family_name
    const font_category = get(user_categorized_families)?.find(font => font.family_name === font_family_name)?.category
    if (font_category) {
      font.category = font_category
      font.categorized = true
    }
  })
  detected_fonts.set(get(detected_fonts))
  console.log('Classifier - Added font categories to detected fonts')
}


export const getUnClassifiedFontFamilyNames = () => {
  // check detected_font_family_names against categorized_font_family_names to find uncategorized fonts
  const detected_font_family_names = uniq(get(detected_fonts).map(font => font.family_name))
  const categorized_font_family_names = get(user_categorized_families)?.map(font => font.family_name) || []
  return difference(detected_font_family_names, categorized_font_family_names)
}

function parseJSON(json) {
  let parsed
  try { parsed = JSON.parse(json) }
  catch (error) { console.warn('Invalid json: ' + json + ' - Error: ' + error) }
  return parsed
}